import React, { useEffect, useRef, useState } from 'react';
import { COLORS } from '../utils/constants';

export interface ExportMenuItem {
  label: string;
  onSelect: () => void;
  /** Optional secondary line describing the output. */
  hint?: string;
  /** Draw a separator above this item to group it apart from the ones before. */
  separated?: boolean;
}

interface ExportMenuProps {
  items: ExportMenuItem[];
  buttonStyle: React.CSSProperties;
}

/** Header "Export" button that opens a dropdown of output options (print, CSV, JSON). */
export const ExportMenu: React.FC<ExportMenuProps> = ({ items, buttonStyle }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(prev => !prev)}
        title="Export the harness (print report, CSV, or project backup)"
        style={{ ...buttonStyle, backgroundColor: COLORS.PRIMARY, color: COLORS.HEADER }}
        onMouseOver={(e) => (e.currentTarget.style.backgroundColor = COLORS.PRIMARY_HOVER)}
        onMouseOut={(e) => (e.currentTarget.style.backgroundColor = COLORS.PRIMARY)}
      >
        Export ▾
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            backgroundColor: 'white',
            border: '1px solid #ddd',
            borderRadius: '6px',
            boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
            zIndex: 10000,
            minWidth: '220px',
            padding: '4px 0'
          }}
        >
          {items.map((item, index) => (
            <div
              key={item.label}
              onClick={() => {
                setOpen(false);
                item.onSelect();
              }}
              style={{
                padding: '8px 14px',
                cursor: 'pointer',
                color: '#2d3436',
                borderTop: item.separated && index > 0 ? '1px solid #eee' : 'none'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f8f9fa')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'white')}
            >
              <div style={{ fontSize: '14px', fontWeight: 500 }}>{item.label}</div>
              {item.hint && (
                <div style={{ fontSize: '12px', color: COLORS.SECONDARY, marginTop: '2px' }}>
                  {item.hint}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
